import { useEffect } from 'react';

interface SeoOptions {
  title: string;
  description: string;
  keywords: string[];
  path: string;
  ogType?: string;
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
}

const MANAGED_ATTR = 'data-seo-managed';

const upsertMeta = (key: 'name' | 'property', value: string, content: string) => {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${key}="${value}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(key, value);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
  el.setAttribute(MANAGED_ATTR, 'true');
};

const upsertLink = (rel: string, href: string) => {
  let el = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
  el.setAttribute(MANAGED_ATTR, 'true');
};

const resolveBase = () => {
  const origin = window.location.origin;
  const basePath = (__BASE_PATH__ || '/').replace(/\/$/, '');
  return `${origin}${basePath}`;
};

export function useSeo({ title, description, keywords, path, ogType = 'website', jsonLd }: SeoOptions) {
  useEffect(() => {
    const base = resolveBase();
    const canonical = `${base}${path === '/' ? '/' : path}`;

    document.title = title;
    document.documentElement.lang = 'zh-CN';

    upsertMeta('name', 'description', description);
    upsertMeta('name', 'keywords', keywords.join('，'));
    upsertMeta('name', 'robots', 'index,follow,max-image-preview:large');
    upsertMeta('name', 'theme-color', '#a83a30');

    upsertLink('canonical', canonical);

    upsertMeta('property', 'og:site_name', '17c吃瓜');
    upsertMeta('property', 'og:title', title);
    upsertMeta('property', 'og:description', description);
    upsertMeta('property', 'og:type', ogType);
    upsertMeta('property', 'og:url', canonical);
    upsertMeta('property', 'og:locale', 'zh_CN');

    upsertMeta('name', 'twitter:card', 'summary_large_image');
    upsertMeta('name', 'twitter:title', title);
    upsertMeta('name', 'twitter:description', description);

    const scriptId = 'seo-json-ld';
    document.getElementById(scriptId)?.remove();
    if (jsonLd) {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.id = scriptId;
      script.textContent = JSON.stringify(jsonLd);
      document.head.appendChild(script);
    }
  }, [title, description, keywords, path, ogType, jsonLd]);
}